import React, { useState, useEffect } from 'react';
import { masterAPI } from '../services/api';
import DataTable from '../components/ui/DataTable';
import toast from 'react-hot-toast';
import { Plus, Building2, X, Save, Edit2, Trash2 } from 'lucide-react';

const EMPTY_FORM = {
  branchName: '', branchCode: '', city: '', phone: ''
};

export default function BranchesPage() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await masterAPI.getBranches();
      setData(res.data.data || []);
    } catch { 
      toast.error('Failed to load branches'); 
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => { 
    load(); 
  }, []);

  const openAdd = () => { 
    setEditItem(null);
    setForm(EMPTY_FORM); 
    setModal(true); 
  };

  const openEdit = (r) => {
    setEditItem(r);
    setForm({
      branchName: r.branchName || '',
      branchCode: r.branchCode || '',
      city: r.city || '',
      phone: r.phone || '',
    });
    setModal(true);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Are you sure you want to delete branch "${r.branchName}"?`)) return;
    try {
      await masterAPI.deleteBranch(r.branchID);
      toast.success('Branch deleted');
      load();
    } catch {
      toast.error('Failed to delete branch');
    }
  };

  const handleInputChange = (field, val) => {
    setForm(prev => ({ ...prev, [field]: val }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.branchName) return toast.error('Branch name is required');
    setSaving(true);
    try {
      if (editItem) {
        await masterAPI.updateBranch(editItem.branchID, form);
        toast.success('Branch updated successfully');
      } else {
        await masterAPI.createBranch(form);
        toast.success('Branch created successfully');
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save branch');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { header: 'Branch Code', key: 'branchCode', render: r => <code className="text-mono" style={{ fontSize: 12 }}>{r.branchCode}</code> },
    { header: 'Branch Name', key: 'branchName', render: r => <span style={{ fontWeight: 600 }}>{r.branchName}</span> },
    { header: 'City', key: 'city', render: r => r.city || <span className="text-muted">—</span> },
    { header: 'Phone', key: 'phone', render: r => r.phone || <span className="text-muted">—</span> },
    { header: 'Status', key: 'isActive', align: 'center',
      render: r => <span className={`badge ${r.isActive !== false ? 'badge-green' : 'badge-red'}`}>{r.isActive !== false ? 'Active' : 'Inactive'}</span> },
    {
      header: 'Actions', align: 'center', render: r => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button className="btn btn-ghost btn-sm btn-icon" title="Edit" onClick={() => openEdit(r)}>
            <Edit2 size={14} />
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" title="Delete" style={{ color: 'var(--red)' }} onClick={() => handleDelete(r)}>
            <Trash2 size={14} />
          </button>
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="flex-between mb-6">
        <div>
          <h1>Branch Management</h1>
          <p className="text-muted">{data.length} total branches</p>
        </div>
        <button className="btn btn-amber" onClick={openAdd}>
          <Plus size={16} /> Add Branch
        </button>
      </div>

      <div className="card">
        <div style={{ padding: '16px 20px' }}>
          <DataTable 
            data={data} 
            columns={columns} 
            loading={loading} 
            searchKeys={['branchName', 'branchCode', 'city']} 
            emptyIcon={<Building2 size={36} />} 
            emptyText="No branches found" 
          />
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3>{editItem ? 'Edit Branch' : 'Add New Branch'}</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Branch Name *</label>
                    <input 
                      className="form-control" 
                      required 
                      value={form.branchName} 
                      onChange={e => handleInputChange('branchName', e.target.value)} 
                      placeholder="e.g. Surat Showroom"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Branch Code (Optional)</label>
                    <input 
                      className="form-control" 
                      value={form.branchCode} 
                      onChange={e => handleInputChange('branchCode', e.target.value)} 
                      placeholder="e.g. BR-SRT"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input 
                      className="form-control" 
                      value={form.city} 
                      onChange={e => handleInputChange('city', e.target.value)} 
                      placeholder="e.g. Surat"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input 
                      className="form-control" 
                      value={form.phone} 
                      onChange={e => handleInputChange('phone', e.target.value)} 
                      placeholder="e.g. +91 9988776655"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <Save size={15} />}
                  {editItem ? 'Save Changes' : 'Create Branch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
